import React, { useState, useEffect } from 'react';
import { Veiculo } from '../../types/veiculo';
import { HistoricoMotoristaVeiculo } from '../../types/veiculoHistorico';
import { veiculoHistoricoMotoristaService } from '../../services/veiculoHistoricoMotoristaService';
import { 
  History, 
  User, 
  Calendar, 
  Truck, 
  CheckCircle2, 
  Clock, 
  Plus, 
  ShieldCheck, 
  X, 
  AlertCircle,
  TrendingUp,
  FileText
} from 'lucide-react';

interface Props {
  veiculo: Veiculo | null;
  isOpen: boolean;
  onClose: () => void;
  motoristasList: { id: number; nome: string }[];
  onMotoristaAtualizado?: (novoMotoristaId: number, novoMotoristaNome: string) => void | Promise<void>;
}

export const VeiculoHistoricoMotoristasModal: React.FC<Props> = ({
  veiculo,
  isOpen,
  onClose,
  motoristasList,
  onMotoristaAtualizado,
}) => {
  const [historico, setHistorico] = useState<HistoricoMotoristaVeiculo[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNovaTroca, setShowNovaTroca] = useState(false);
  const [novoMotoristaId, setNovoMotoristaId] = useState<number | ''>('');
  const [dataTroca, setDataTroca] = useState(new Date().toISOString().split('T')[0]);
  const [motivo, setMotivo] = useState('Assumiu a titularidade da rota');
  const [salvando, setSalvando] = useState(false);
  const [feedbackMsg, setFeedbackMsg] = useState<{ tipo: 'success' | 'error'; texto: string } | null>(null);

  useEffect(() => {
    if (isOpen && veiculo) {
      carregarHistorico();
      setShowNovaTroca(false);
      setNovoMotoristaId('');
      setFeedbackMsg(null);
    }
  }, [isOpen, veiculo]);

  const carregarHistorico = async () => {
    if (!veiculo) return;
    try {
      setLoading(true);
      const data = await veiculoHistoricoMotoristaService.getHistoricoPorVeiculo(veiculo.id, veiculo.placa || undefined);
      setHistorico(data);
    } catch (err) {
      console.error('Erro ao carregar histórico:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSalvarTroca = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!veiculo || !novoMotoristaId) return;

    const motObj = motoristasList.find(m => m.id === Number(novoMotoristaId));
    const motNome = motObj ? motObj.nome : `Motorista #${novoMotoristaId}`;

    try {
      setSalvando(true);
      setFeedbackMsg(null);

      // Registra a troca no serviço de histórico
      await veiculoHistoricoMotoristaService.registrarTrocaMotorista({
        veiculo_id: veiculo.id,
        placa: veiculo.placa || '',
        novo_motorista_id: Number(novoMotoristaId),
        novo_motorista_nome: motNome,
        data_troca: dataTroca,
        motivo: motivo.trim(),
      });

      // Aplica o novo condutor no cadastro do veículo. Precisa ser aguardado:
      // sem isso a mensagem de sucesso aparecia antes (e mesmo que falhasse).
      if (onMotoristaAtualizado) {
        await onMotoristaAtualizado(Number(novoMotoristaId), motNome);
      }

      setFeedbackMsg({
        tipo: 'success',
        texto: `Motorista ${motNome} definido com sucesso para o veículo ${veiculo.placa || ''}! As ordens de serviço anteriores continuam preservadas.`
      });

      setShowNovaTroca(false);
      setNovoMotoristaId('');
      await carregarHistorico();
    } catch (err: any) {
      console.error('Erro ao salvar troca de motorista:', err);
      setFeedbackMsg({
        tipo: 'error',
        texto: 'Falha ao registrar a troca de motorista. Tente novamente.'
      });
    } finally {
      setSalvando(false);
    }
  };

  const formatDate = (d?: string | null) => {
    if (!d) return '-';
    try {
      const parts = d.split('T')[0].split('-');
      if (parts.length === 3) {
        return `${parts[2]}/${parts[1]}/${parts[0]}`;
      }
      return d;
    } catch {
      return d;
    }
  };

  if (!isOpen || !veiculo) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
      <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col max-h-[90vh] animate-in fade-in zoom-in-95 duration-200">
        
        {/* Header */}
        <div className="px-6 py-4 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white flex items-center justify-between border-b border-slate-700">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-red-600 rounded-xl shadow-inner">
              <History className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-base font-bold flex items-center gap-2">
                Histórico de Motoristas do Veículo
                <span className="px-2.5 py-0.5 bg-slate-800 text-amber-400 font-mono text-xs rounded-md border border-slate-700 font-black">
                  {veiculo.placa || 'Sem Placa'}
                </span>
              </h3>
              <p className="text-xs text-slate-300">
                Linha do tempo de condutores e viagens vinculadas a este veículo
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Info Banner: Preservação de Histórico */}
        <div className="bg-blue-50 border-b border-blue-100 px-6 py-2.5 flex items-center gap-2.5 text-blue-900 text-xs">
          <ShieldCheck className="w-4 h-4 text-blue-600 shrink-0" />
          <span>
            <strong>Histórico Blindado:</strong> As trocas de motorista registradas aqui mantêm o registro original em todas as Ordens de Serviço passadas intacto.
          </span>
        </div>

        {/* Content Body */}
        <div className="p-6 overflow-y-auto space-y-6 flex-1">
          
          {feedbackMsg && (
            <div className={`p-3.5 rounded-xl text-xs flex items-center gap-2.5 ${
              feedbackMsg.tipo === 'success' ? 'bg-emerald-50 text-emerald-900 border border-emerald-200' : 'bg-rose-50 text-rose-900 border border-rose-200'
            }`}>
              {feedbackMsg.tipo === 'success' ? <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" /> : <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />}
              <span>{feedbackMsg.texto}</span>
            </div>
          )}

          {/* Action to add driver switch */}
          {!showNovaTroca ? (
            <div className="flex items-center justify-between bg-slate-50 p-4 rounded-xl border border-slate-200/80">
              <div>
                <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                  Precisa atribuir outro motorista a este veículo?
                </h4>
                <p className="text-xs text-slate-500 mt-0.5">
                  Adicione um novo condutor sem interferir nas viagens já concluídas por outros motoristas.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowNovaTroca(true)}
                className="px-3 py-2 bg-red-600 text-white rounded-lg text-xs font-bold hover:bg-red-700 flex items-center gap-1.5 transition-colors shadow-2xs cursor-pointer shrink-0"
              >
                <Plus className="w-4 h-4" /> Registrar Novo Motorista
              </button>
            </div>
          ) : (
            <form onSubmit={handleSalvarTroca} className="bg-slate-50 p-4 rounded-xl border border-red-200 space-y-3">
              <div className="flex items-center justify-between pb-2 border-b border-slate-200">
                <span className="text-xs font-bold text-red-700 uppercase flex items-center gap-1.5">
                  <User className="w-4 h-4" /> Novo Motorista para a Placa {veiculo.placa}
                </span>
                <button
                  type="button"
                  onClick={() => setShowNovaTroca(false)}
                  className="text-xs text-slate-400 hover:text-slate-700"
                >
                  Cancelar
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                    Selecione o Motorista *
                  </label>
                  <select
                    value={novoMotoristaId}
                    onChange={(e) => setNovoMotoristaId(e.target.value ? Number(e.target.value) : '')}
                    required
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-xs bg-white focus:ring-2 focus:ring-red-500 outline-none font-medium"
                  >
                    <option value="">Selecione um condutor...</option>
                    {motoristasList.map(m => (
                      <option key={m.id} value={m.id}>{m.nome}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                    Data de Início / Troca *
                  </label>
                  <input
                    type="date"
                    value={dataTroca}
                    onChange={(e) => setDataTroca(e.target.value)}
                    required
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-xs bg-white focus:ring-2 focus:ring-red-500 outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                  Motivo / Observação
                </label>
                <input
                  type="text"
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value)}
                  placeholder="Ex: Assumiu titularidade, Cobertura de férias, Manutenção"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-xs bg-white focus:ring-2 focus:ring-red-500 outline-none"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowNovaTroca(false)}
                  className="px-3 py-1.5 border border-slate-300 rounded-lg text-xs font-semibold text-slate-600 hover:bg-slate-100"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={salvando || !novoMotoristaId}
                  className="px-4 py-1.5 bg-red-600 text-white rounded-lg text-xs font-bold hover:bg-red-700 disabled:opacity-50 flex items-center gap-1.5"
                >
                  {salvando ? 'Salvando...' : 'Salvar Alteração'}
                </button>
              </div>
            </form>
          )}

          {/* Timeline List */}
          <div>
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-1.5">
              <History className="w-3.5 h-3.5" /> Linha do Tempo de Condutores
            </h4>

            {loading ? (
              <div className="py-12 text-center text-slate-400 text-xs">
                Carregando histórico do veículo...
              </div>
            ) : historico.length === 0 ? (
              <div className="py-8 px-4 text-center bg-slate-50 border border-dashed border-slate-200 rounded-xl text-slate-500 text-xs">
                Nenhum histórico registrado ainda para este veículo.
              </div>
            ) : (
              <div className="relative border-l-2 border-slate-200 ml-4 space-y-6 pb-2">
                {historico.map((item, idx) => {
                  const isCurrent = item.is_atual || idx === 0;
                  return (
                    <div key={item.id || idx} className="relative pl-6">
                      {/* Timeline Dot */}
                      <span className={`absolute -left-[9px] top-1 w-4 h-4 rounded-full border-2 border-white flex items-center justify-center ${
                        isCurrent ? 'bg-emerald-500 ring-4 ring-emerald-100' : 'bg-slate-400 ring-2 ring-slate-100'
                      }`} />

                      <div className={`p-4 rounded-xl border transition-all ${
                        isCurrent 
                          ? 'bg-emerald-50/40 border-emerald-200 shadow-2xs' 
                          : 'bg-white border-slate-200 shadow-2xs hover:border-slate-300'
                      }`}>
                        <div className="flex items-start justify-between gap-2 flex-wrap">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-bold text-slate-900">
                                {item.motorista_nome || 'Condutor Não Identificado'}
                              </span>
                              {isCurrent && (
                                <span className="px-2 py-0.5 bg-emerald-600 text-white text-3xs font-black uppercase rounded-full tracking-wide">
                                  Atual / Em Atividade
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-slate-500 mt-0.5 italic">
                              {item.motivo || 'Período de condução'}
                            </p>
                          </div>

                          {/* Stats badge */}
                          {item.total_os !== undefined && item.total_os > 0 && (
                            <div className="flex items-center gap-1.5 px-2.5 py-1 bg-slate-100 border border-slate-200 rounded-lg text-3xs font-bold text-slate-700">
                              <TrendingUp className="w-3 h-3 text-red-600" />
                              <span>{item.total_os} {item.total_os === 1 ? 'viagem' : 'viagens'} realizadas</span>
                            </div>
                          )}
                        </div>

                        {/* Dates & Range */}
                        <div className="mt-3 pt-2.5 border-t border-slate-100 flex items-center justify-between text-3xs text-slate-500 font-medium flex-wrap gap-2">
                          <div className="flex items-center gap-1.5">
                            <Calendar className="w-3.5 h-3.5 text-slate-400" />
                            <span>
                              {item.data_inicio ? `Início: ${formatDate(item.data_inicio)}` : 'Início não datado'}
                              {item.data_fim ? ` até ${formatDate(item.data_fim)}` : isCurrent ? ' (Até o momento)' : ''}
                            </span>
                          </div>

                          {(item.primeira_os_data || item.ultima_os_data) && (
                            <div className="flex items-center gap-1 text-slate-400">
                              <FileText className="w-3 h-3" />
                              <span>OSs: {formatDate(item.primeira_os_data)} ~ {formatDate(item.ultima_os_data)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-3 bg-slate-50 border-t border-slate-200 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-slate-800 text-white rounded-xl text-xs font-bold hover:bg-slate-700 transition-colors shadow-2xs"
          >
            Fechar
          </button>
        </div>

      </div>
    </div>
  );
};
